const getViewerName = (user) => {
  const name = typeof user?.name === "string" ? user.name.trim() : "";

  return name || user?.id || "Unknown user";
};

// Stream stamps a new member's last_read at the moment they join (so they
// don't inherit an unread backlog), which reads as "viewed" for every earlier
// message. That synthetic read only carries a timestamp: a real mark-read from
// a client also records last_read_message_id. So a read state is treated as
// the join-time stamp only when it has no last_read_message_id AND still sits
// at the join time (short tolerance for the server writing both records), or
// when it covers only messages that predate the join. A read that moved past
// a message sent after the join is genuine either way.
const AUTO_READ_GRACE_MS = 5000;

export const hasOnlyAutoReadOnJoin = (readState, membership, messageCreatedAt) => {
  const lastRead = readState?.last_read ? new Date(readState.last_read).getTime() : NaN;
  const joinedAt = membership?.created_at ? new Date(membership.created_at).getTime() : NaN;
  if (Number.isNaN(lastRead) || Number.isNaN(joinedAt)) return false;
  // a client mark-read always records the message it read up to
  if (readState?.last_read_message_id) return false;
  if (lastRead - joinedAt > AUTO_READ_GRACE_MS) return false;
  const createdAt = messageCreatedAt ? new Date(messageCreatedAt).getTime() : NaN;
  // unknown message time: keep the conservative behaviour
  if (Number.isNaN(createdAt)) return true;
  return createdAt <= joinedAt;
};

export const getMessageViewers = (
  readBy = [],
  currentUserId,
  senderId,
  { readState = {}, members = {}, messageCreatedAt } = {}
) => {
  const viewersById = new Map();

  readBy.forEach((user) => {
    if (
      !user?.id ||
      user.id === (senderId || currentUserId) ||
      (senderId && user.id === senderId) ||
      viewersById.has(user.id) ||
      hasOnlyAutoReadOnJoin(readState?.[user.id], members?.[user.id], messageCreatedAt)
    ) {
      return;
    }

    viewersById.set(user.id, user);
  });

  return Array.from(viewersById.values()).sort((firstUser, secondUser) =>
    getViewerName(firstUser).localeCompare(getViewerName(secondUser), undefined, {
      sensitivity: "base",
    })
  );
};

export { getViewerName };
